import React from "react";

import { DateTime } from "luxon";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useBanners from "@app/analytics/hooks/useBanners";
import Link from "@app/common/components/Link";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import type { BillingCurrentPlan, UserBillingAccount } from "@app/common/types";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import { Bold } from "@rui/styles";

const freeTrialIsExpired = (
  billingAccount: UserBillingAccount | null,
  currentPlan: BillingCurrentPlan,
  loading: boolean
) => {
  if (!billingAccount || !billingAccount?.freeTrialExpirationDate) return false;

  const daysSinceTrialExpiration = DateTime.fromISO(
    billingAccount.freeTrialExpirationDate
  )
    .diffNow()
    .as("days");

  // don't show the alert if the trial expired more than a month ago
  // and the user hasn't dismissed the alert yet. they get it.
  if (Math.abs(daysSinceTrialExpiration) > 30) {
    return false;
  }

  return !loading && isFreePlan(currentPlan.type, true);
};

const FreeTrialEndedAlert = ({ priority }: { priority: number }) => {
  const { billingAccount, currentPlan, plansInitialLoading } =
    useBillingContext();
  const [freeTrialEndedDismissed, setFreeTrialEndedDismissed] = useLocalStorage(
    "freeTrialEndedDismissed",
    false
  );

  const shouldShow =
    !freeTrialEndedDismissed &&
    freeTrialIsExpired(billingAccount, currentPlan, plansInitialLoading);

  const { show } = useBanners({
    id: "freeTrialEnded",
    priority,
    shouldShow,
  });

  if (!show) return null;

  const handleDismiss = () => setFreeTrialEndedDismissed(true);

  return (
    <Alert
      severity="error"
      dismissible
      onDismiss={handleDismiss}
      action={
        <Link to={ROUTES.UPGRADE_ACCOUNT} openInBrowser>
          <Button
            variant="contained"
            color="error"
            sx={{ width: "124px", height: "40px" }}
          >
            Upgrade Plan
          </Button>
        </Link>
      }
    >
      <Bold>Free trial ended:</Bold> Your Read free trial has ended and your
      account has been downgraded to a Free plan. Upgrade to regain access to
      advanced features.{" "}
      <Link
        href={LINKS.MARKETING.PLANS_PRICING}
        sx={{
          textDecoration: "underline",
          color: "shades.primary.500",
        }}
      >
        See the benefits of upgrading
      </Link>
      .
    </Alert>
  );
};

export default FreeTrialEndedAlert;
